"use client"
import DashboardTemplate from "../templates/DashboardTemplate"
import useFetch from "../../hooks/useFetch"
import { useAuth } from "../../hooks/useAuth"
import { Bell, AlertCircle, CheckCircle, Info } from "lucide-react"

const Spinner = () => (
  <div className="flex justify-center items-center py-8">
    <svg
      className="animate-spin h-10 w-10 text-amber-500"
      xmlns="http://www.w3.org/2000/svg"
      fill="none"
      viewBox="0 0 24 24"
    >
      <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
      <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8v8z"></path>
    </svg>
  </div>
)

const NotificationCard = ({ notification, index }) => {
  // Determine icon and color based on notification type or priority
  const getNotificationConfig = () => {
    const type = notification.type || "info"

    switch (type.toLowerCase()) {
      case "error":
      case "danger":
        return {
          icon: AlertCircle,
          bgColor: "bg-red-50",
          borderColor: "border-red-200",
          iconColor: "text-red-500",
        }
      case "success":
        return {
          icon: CheckCircle,
          bgColor: "bg-green-50",
          borderColor: "border-green-200",
          iconColor: "text-green-500",
        }
      case "warning":
        return {
          icon: AlertCircle,
          bgColor: "bg-amber-50",
          borderColor: "border-amber-200",
          iconColor: "text-amber-500",
        }
      case "info":
      default:
        return {
          icon: Info,
          bgColor: "bg-blue-50",
          borderColor: "border-blue-200",
          iconColor: "text-blue-500",
        }
    }
  }

  const { icon: Icon, bgColor, borderColor, iconColor } = getNotificationConfig()
  const date = notification.createdAt ? new Date(notification.createdAt) : new Date()

  return (
    <div
      className={`${bgColor} border ${borderColor} rounded-lg p-4 mb-4 animate-fadeIn`}
      style={{ animationDelay: `${index * 100}ms` }}
    >
      <div className="flex">
        <div className={`${iconColor} mr-3 flex-shrink-0`}>
          <Icon className="h-6 w-6" />
        </div>
        <div className="flex-1">
          <h3 className="text-sm font-medium text-gray-900">{notification.title || "Notification"}</h3>
          <div className="mt-1 text-sm text-gray-600">{notification.message || "New recommendation!"}</div>
          <div className="mt-2 text-xs text-gray-500">{date.toLocaleString()}</div>
        </div>
      </div>
    </div>
  )
}

const NotificationsPage = () => {
  const { user } = useAuth()
  // Fetch recommendations using the user's ID. Adjust endpoint as needed.
  const {
    data: notifications,
    loading,
    error,
  } = useFetch(user ? `/ai/recommendations?userId=${user._id}` : "/ai/recommendations")

  return (
    <DashboardTemplate>
      <div className="p-6">
        <div className="flex items-center mb-6">
          <Bell className="h-7 w-7 mr-2 text-amber-600" />
          <h1 className="text-3xl font-bold text-gray-800">Notifications & Recommendations</h1>
        </div>

        {loading && <Spinner />}

        {error && (
          <div className="bg-red-100 border border-red-400 text-red-700 px-4 py-3 rounded-lg mb-4">
            <div className="flex">
              <AlertCircle className="h-5 w-5 mr-2" />
              <span>Error: {error.message}</span>
            </div>
          </div>
        )}

        {notifications && notifications.recommendations ? (
          <div className="space-y-4">
            {notifications.recommendations.length > 0 ? (
              notifications.recommendations.map((notification, index) => (
                <NotificationCard key={notification._id || index} notification={notification} index={index} />
              ))
            ) : (
              <div className="bg-amber-50 border border-amber-200 rounded-lg p-8 text-center">
                <Bell className="mx-auto h-12 w-12 text-amber-500 mb-3" />
                <h3 className="text-lg font-medium text-gray-900">No notifications yet</h3>
                <p className="mt-2 text-sm text-gray-500">
                  We'll notify you when there are new recommendations or updates.
                </p>
              </div>
            )}
          </div>
        ) : (
          !loading && (
            <div className="bg-amber-50 border border-amber-200 rounded-lg p-8 text-center">
              <Bell className="mx-auto h-12 w-12 text-amber-500 mb-3" />
              <h3 className="text-lg font-medium text-gray-900">No notifications found</h3>
              <p className="mt-2 text-sm text-gray-500">
                We'll notify you when there are new recommendations or updates.
              </p>
            </div>
          )
        )}
      </div>

      <style jsx>{`
        @keyframes fadeIn {
          from { opacity: 0; transform: translateY(10px); }
          to { opacity: 1; transform: translateY(0); }
        }
        .animate-fadeIn {
          animation: fadeIn 0.3s ease-out forwards;
        }
      `}</style>
    </DashboardTemplate>
  )
}

export default NotificationsPage
